import { useEffect, useRef, useState } from "react";

/**
 * v2 panelinin kendi sayfa görüntüleyicisi: bir PDF sayfasını canvas'a çizer ve üstüne
 * normalize dikdörtgenleri bindirir.
 *
 * **Neden ayrı bir bileşen.** v2 paneli cutover'dan (F10) sonra tek tüketici olacak ve o gün
 * eski gövdenin silinmesi bir yetenek kaybına yol açmamalı. Bu bileşen o günün hazırlığı:
 * kendi kendine yeterli, eski bileşenin state makinesine bağlı değil.
 *
 * **PDF'i KENDİSİ indirmiyor.** Proje kuralı gereği ağ çağrıları servis katmanından geçiyor
 * ve ebeveyn zaten iki tarafın PDF'ini yüklüyor; ikinci bir indirme hem kuralı çiğnerdi hem
 * de aynı baytları iki kez çekerdi. Bileşen ayrıştırılmış pdfjs dokümanını alıyor.
 *
 * **pdfjs yüzeyi için dar arayüzler tanımlı, `any` yok.** Kullandığımız üç metot dışında
 * hiçbir şeye bağımlı değiliz; kütüphanenin tipleri değişse bile derleyici bize kullandığımız
 * yüzeyi gösterir.
 */

export interface PdfViewport {
  width: number;
  height: number;
}

interface PdfViewportParams {
  scale: number;
}

interface PdfRenderParams {
  canvasContext: CanvasRenderingContext2D;
  viewport: PdfViewport;
}

export interface PdfRenderTask {
  promise: Promise<void>;
  cancel(): void;
}

export interface PdfPageProxy {
  getViewport(params: PdfViewportParams): PdfViewport;
  render(params: PdfRenderParams): PdfRenderTask;
}

export interface PdfDocumentProxy {
  numPages: number;
  getPage(pageNumber: number): Promise<PdfPageProxy>;
}

/** Sayfa üstünde işaretlenecek bir alan. Koordinatlar normalize (0-1). */
export interface HighlightRect {
  /** [x, y, w, h] — 0-1, sayfa boyutuna oran. Pixel DEĞİL. */
  rect: number[];
  color: string;
  /** Fareyle üzerine gelindiğinde gösterilecek metin. */
  title: string;
  /** Vurgulanan bulgu seçili mi — seçili olan daha belirgin çizilir. */
  selected: boolean;
}

interface PdfPageCanvasProps {
  document: PdfDocumentProxy | null;
  /** 1-tabanlı sayfa numarası. */
  page: number;
  highlights: HighlightRect[];
  /** Görüntüleyicinin kapsayıcı genişliği (CSS pixel). Sayfa buna göre ölçekleniyor. */
  width: number;
  /** Boş durum ve hata metinleri için dil. */
  isTr: boolean;
}

export function PdfPageCanvas(props: PdfPageCanvasProps): JSX.Element {
  const { document: pdfDocument, page, highlights, width, isTr } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const taskRef = useRef<PdfRenderTask | null>(null);
  const [size, setSize] = useState<PdfViewport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect((): (() => void) => {
    let cancelled = false;

    const draw = async (): Promise<void> => {
      const canvas = canvasRef.current;
      if (!pdfDocument || !canvas || width <= 0) return;
      if (page < 1 || page > pdfDocument.numPages) {
        setError(isTr ? `Sayfa ${page} bu belgede yok` : `Page ${page} is not in this document`);
        return;
      }

      // Ayni canvas uzerinde eszamanli render pdfjs'te hata firlatiyor; onceki isi
      // iptal edip bitmesini bekliyoruz.
      const previous = taskRef.current;
      if (previous) {
        try {
          previous.cancel();
          await previous.promise;
        } catch {
          // Iptal reddetmeyle sonuclanir; beklenen durum.
        }
      }
      if (cancelled) return;

      try {
        const pdfPage: PdfPageProxy = await pdfDocument.getPage(page);
        if (cancelled) return;

        // Cihaz pixel orani hesaba katiliyor: aksi halde yuksek DPI ekranlarda sayfa
        // bulanik ciziliyor ve ince punto metin okunamiyor.
        const ratio: number = Math.max(1, window.devicePixelRatio || 1);
        const native: PdfViewport = pdfPage.getViewport({ scale: 1 });
        const cssScale: number = native.width > 0 ? width / native.width : 1;
        const viewport: PdfViewport = pdfPage.getViewport({ scale: cssScale * ratio });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
        canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;

        const context: CanvasRenderingContext2D | null = canvas.getContext("2d");
        if (!context) {
          setError(isTr ? "Canvas bağlamı alınamadı" : "Canvas context unavailable");
          return;
        }

        const task: PdfRenderTask = pdfPage.render({ canvasContext: context, viewport });
        taskRef.current = task;
        await task.promise;
        if (cancelled) return;
        setError(null);
        setSize({ width: Math.floor(viewport.width / ratio), height: Math.floor(viewport.height / ratio) });
      } catch (renderError: unknown) {
        if (cancelled) return;
        const message: string = renderError instanceof Error ? renderError.message : String(renderError);
        // Iptal edilen render bir hata degil.
        if (message.toLowerCase().includes("cancel")) return;
        setError(message);
      }
    };

    void draw();
    return (): void => {
      cancelled = true;
      const running = taskRef.current;
      if (running) {
        try {
          running.cancel();
        } catch {
          // Bilesen soktuluyor; iptalin sonucu onemli degil.
        }
      }
    };
  }, [pdfDocument, page, width, isTr]);

  if (!pdfDocument) {
    return (
      <div style={{ color: "#6b7280", padding: 12 }}>
        {isTr ? "Sayfa görüntüsü yüklenmedi." : "Page image not loaded."}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <canvas ref={canvasRef} style={{ display: "block", maxWidth: "100%" }} />
      {error && (
        <div style={{ color: "#b91c1c", padding: "6px 0" }}>
          {isTr ? "Sayfa çizilemedi: " : "Could not render page: "}
          {error}
        </div>
      )}
      {size && highlights.length > 0 && (
        /*
          Vurgular SVG olarak canvas'in UZERINE biniyor. Koordinatlar normalize geldigi
          icin viewBox 0-1: olcek degisince dikdortgenler kendiliginden dogru yerde kaliyor
          ve hicbir yerde pixel donusumu tekrarlanmiyor. Eski motorda her tuketici bolmeyi
          kendi yapiyordu ve bolmeyi atlayan yer sessizce yanlis yeri isaretliyordu.
        */
        <svg
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: size.width,
            height: size.height,
            pointerEvents: "none"
          }}
        >
          {highlights.map((highlight: HighlightRect, index: number) => (
            <rect
              key={index}
              x={highlight.rect[0]}
              y={highlight.rect[1]}
              width={highlight.rect[2]}
              height={highlight.rect[3]}
              fill={highlight.color}
              fillOpacity={highlight.selected ? 0.24 : 0.1}
              stroke={highlight.color}
              strokeWidth={highlight.selected ? 0.004 : 0.002}
            >
              <title>{highlight.title}</title>
            </rect>
          ))}
        </svg>
      )}
    </div>
  );
}
